import React, { useCallback, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { ShowSuccessFetch, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import {
    deleteHRM2130_R01, downloadExcel_HRM2130_R01, getHRM2130_R01AllCount,
    getHRM2130_R01List, patchHRM2130_R01, postHRM2130_R01,
} from "./Api_HRM2130_R01";
import { defaultHRM2130_R01Dto, HRM2130_R01Dto } from "./Dto_HRM2130_R01";
import { defaultHRM2130_R01Filter, HRM2130_R01Filter, useHRM2130_R01Store } from "./Store_HRM2130_R01";
import { ATTEND_GROUP_OUTING } from "./Util_HRM2130_R01";

interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2130_R01Dto> | null>;
    setMessage: (message: string) => void;
}
type FetchOptions = { messageMode?: 'show' | 'none' };

/**
 * 화면의 조회, 등록, 삭제 표시, 일괄 저장, Excel 동작을 구성한다.
 * @param gridRef 편집 확정과 조회 후 스크롤 이동에 사용할 그리드 참조
 * @param setMessage 조회·저장 결과를 표시하는 화면 메시지 변경 함수
 * @returns Container가 버튼과 그리드에 연결할 상태 및 동작 함수
 */
export const HRM2130_R01Content = ({ gridRef, setMessage }: Props) => {
    const {
        HRM2130_R01Filter, setHRM2130_R01PastFilter,
        HRM2130_R01List, setHRM2130_R01List, insertHRM2130_R01List, removeHRM2130_R01List,
        selectedHRM2130_R01List, removeSelectedHRM2130_R01List,
        deletedHRM2130_R01List, addDeletedHRM2130_R01List, removeDeletedHRM2130_R01List,
        setOriginalHRM2130_R01List,
        setAllCount, setScrollTop, initData,
    } = useHRM2130_R01Store();
    const { attendGroupList } = useCommonCodeStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const [clickable, setClickable] = useState(true);

    useFetchActionbarAuth("HRM2130_R01");


    /** 빈 검색값을 undefined로 바꿔 요청에서 제외할 조건 객체를 반환한다. */
    const getParams = useCallback((filter: HRM2130_R01Filter): Partial<HRM2130_R01Filter> => ({
        attendGroup: filter.attendGroup || undefined,
        useYn: filter.useYn || undefined,
        attendNm: filter.attendNm || undefined,
    }), []);

    /** 현재 검색 조건의 전체 건수를 서버에서 조회해 Store에 반영하고 건수를 반환한다. */
    const renewalAllCount = useCallback(async () => {
        const response = await getHRM2130_R01AllCount(getParams(HRM2130_R01Filter));
        // 건수 조회가 실패하면 data가 숫자가 아니므로 0건으로 둔다. 알림은 공통 인터셉터가 띄운다.
        if (!response.data.successFlag) return 0;
        setAllCount(response.data.data);
        return response.data.data;
    }, [getParams, HRM2130_R01Filter, setAllCount]);

    /**
     * 목록과 전체 건수를 같은 조건으로 재조회하고 선택·삭제 표시를 초기화한다.
     * @param fetchType search는 스크롤을 초기화하고 refetch는 저장 뒤 목록을 다시 읽는다.
     * @param options messageMode가 none이면 조회 완료 또는 빈 목록 알림을 표시하지 않는다.
     * @returns 조회 완료를 나타내는 Promise<void>. 오류는 호출자에게 전파한다.
     */
    const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch', options?: FetchOptions) => {
        setLoadingSpinner(true);
        try {
            // 사용자가 새 조회를 요청한 경우에만 이전 스크롤 위치를 버린다.
            if (fetchType === 'search') setScrollTop(null);
            const response = await getHRM2130_R01List(fetchType, getParams(HRM2130_R01Filter), 0);
            // 공통 인터셉터는 실패 응답도 오류 알림 후 그대로 넘겨주므로, 실패하면 기존 목록을 유지하고 끝낸다.
            if (!response.data.successFlag) return;
            const list = response.data.data;
            setHRM2130_R01List(list);
            setOriginalHRM2130_R01List(list);
            useHRM2130_R01Store.getState().initSelectedHRM2130_R01List();
            removeDeletedHRM2130_R01List();
            const count = await renewalAllCount();
            // 목록이 있으면 첫 행으로 이동하고, 요청한 경우에만 완료 메시지를 표시한다.
            if (list.length > 0) {
                gridRef.current?.api.ensureIndexVisible(0, 'top');
                if (options?.messageMode !== 'none') {
                    ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${list.length}/${count}건)`);
                }
            // 결과가 없고 메시지를 허용한 경우에는 빈 목록을 알린다.
            } else if (options?.messageMode !== 'none') {
                showWarning('데이터가 없습니다.');
            }
            setHRM2130_R01PastFilter(HRM2130_R01Filter);
        } finally {
            // 조회 성공·실패와 관계없이 로딩 표시를 해제한다.
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, setScrollTop, getParams, HRM2130_R01Filter, setHRM2130_R01List,
        setOriginalHRM2130_R01List, removeDeletedHRM2130_R01List, renewalAllCount,
        gridRef, setMessage, setHRM2130_R01PastFilter]);

    /** 조회 버튼과 Enter 키에서 일반 조회를 실행하고 조회 Promise를 반환한다. */
    const fetchData = useCallback(() => executeDataFetch('search'), [executeDataFetch]);

    /** 사용·근무인정 Y와 현재 최대 순서 + 1을 가진 신규 행 기본값을 반환한다. */
    const defaultHRM2130_R01Row = useCallback((): HRM2130_R01Dto => ({
        ...defaultHRM2130_R01Dto,
        inserted: true,
        sortSeq: Math.max(0, ...HRM2130_R01List.map(row => row.sortSeq || 0)) + 1,
    }), [HRM2130_R01List]);

    /** 화면 Store와 안내 메시지를 초기화한다. 근태구분 선택지는 공통 Store에서 관리한다. */
    const initContainerData = useCallback(() => {
        initData();
        setMessage('');
    }, [initData, setMessage]);

    /**
     * 등록 버튼 또는 행의 + 버튼으로 신규 행을 만든다.
     * @param rowIndex 값이 있으면 해당 그리드 행을 기준으로 삽입한다.
     * @returns 반환값 없이 Store 목록을 갱신한다.
     */
    const handleInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number) => {
        // 행 번호가 전달되면 행의 + 버튼 동작이므로 해당 행을 삽입 기준으로 사용한다.
        const isGridInsert = rowIndex !== undefined;
        InsertRowHandler({
            event,
            dataList: HRM2130_R01List,
            rowData: isGridInsert ? HRM2130_R01List[rowIndex] : { ...defaultHRM2130_R01Row(), valueIndex: -1 },
            insertRow: insertHRM2130_R01List,
            defaultRowData: defaultHRM2130_R01Row(),
            ...(isGridInsert && { setClickable }),
        });
    }, [HRM2130_R01List, defaultHRM2130_R01Row, insertHRM2130_R01List]);

    /**
     * 저장 대상 행의 필수값, 근태구분, 순서, 코드 중복을 순서대로 검증한다.
     * @param row 검증할 신규 또는 수정 행
     * @param activeRows 삭제 표시와 완전한 빈 신규 행을 제외한 비교 대상
     * @returns 모두 유효하면 true, 첫 실패 항목을 경고하고 저장을 막으면 false
     */
    const validateInsertData = useCallback((row: HRM2130_R01Dto, activeRows: HRM2130_R01Dto[]): boolean => {
        const code = row.attendCd.trim().toUpperCase();
        // 공백 제거 뒤 코드가 없으면 필수값 오류를 알리고 검증을 중단한다.
        if (!code) { showWarning('근태코드를 입력해주세요.'); return false; }
        // 이름이 빈 문자열 또는 공백뿐이면 저장할 수 없다.
        if (!row.attendNm.trim()) { showWarning('근태명을 입력해주세요.'); return false; }
        // 구분이 없거나 ATTEND_GROUP 선택지에 없는 값이면 저장할 수 없다.
        if (!row.attendGroup || !attendGroupList.some(item => item.minorCd === row.attendGroup)) {
            showWarning('근태구분을 선택해주세요.'); return false;
        }
        // 순서는 0 이상의 정수여야 한다.
        if (!Number.isInteger(row.sortSeq) || row.sortSeq < 0) {
            showWarning('순서는 0 이상의 정수여야 합니다.'); return false;
        }
        // 다른 행과 코드를 비교할 때 앞뒤 공백과 영문 대소문자를 무시한다.
        if (activeRows.some(other => other.valueIndex !== row.valueIndex &&
            other.attendCd.trim().toUpperCase() === code)) {
            showWarning('중복된 근태코드가 있습니다. > ' + code); return false;
        }
        return true;
    }, [attendGroupList]);

    /**
     * 편집값을 확정한 뒤 신규·수정·삭제 대상을 분리하여 일괄 요청한다.
     * 빈 신규 행은 제외하고, 검증 실패 또는 변경 없음이면 요청 없이 종료한다.
     * @returns 모든 요청과 성공 후 재조회가 완료되는 Promise<void>. 요청 오류는 전파한다.
     */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        // stopEditing이 발생시킨 최신 셀 값을 읽기 위해 렌더 시점의 목록 대신 Store를 다시 조회한다.
        const currentList = useHRM2130_R01Store.getState().HRM2130_R01List;
        // 삭제 목록에는 숫자(행 클릭 후 삭제)와 문자열(전체 선택 후 삭제)이 섞일 수 있어 문자열로 맞춰 비교한다.
        const deletedSet = new Set(useHRM2130_R01Store.getState().deletedHRM2130_R01List.map(String));
        /** valueIndex 문자열이 삭제 표시 목록에 있으면 true를 반환한다. */
        const isDeleted = (row: HRM2130_R01Dto) => deletedSet.has(row.valueIndex.toString());
        /** 코드·명칭·구분이 모두 비어 있는 신규 그리드 행이면 true를 반환한다. */
        const isBlank = (row: HRM2130_R01Dto) =>
            row.inserted && !row.attendCd.trim() && !row.attendNm.trim() && !row.attendGroup;
        const activeRows = currentList.filter(row => !isDeleted(row) && !isBlank(row));
        const insertedList = activeRows.filter(row => row.inserted);
        const modifiedList = activeRows.filter(row => row.isModified && !row.inserted);
        const deleteTargetList = currentList.filter(row => isDeleted(row) && !row.inserted);

        // 저장 대상이 하나도 없으면 서버 요청을 보내지 않는다.
        if (insertedList.length + modifiedList.length + deleteTargetList.length === 0) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }
        for (const row of [...insertedList, ...modifiedList]) {
            // 한 행이라도 검증에 실패하면 세 종류의 저장 요청을 모두 중단한다.
            if (!validateInsertData(row, activeRows)) return;
        }
        /** 서버에 보낼 코드를 대문자로 정리하고 G5 이외의 근무인정을 Y로 맞춘 행을 반환한다. */
        const normalize = (row: HRM2130_R01Dto): HRM2130_R01Dto => ({
            ...row,
            attendCd: row.attendCd.trim().toUpperCase(),
            workYn: row.attendGroup === ATTEND_GROUP_OUTING ? row.workYn : 'Y',
        });
        // 신규·수정·삭제 대상이 있는 요청만 실행하고 모두 성공해야 완료 처리한다.
        const responseList = await Promise.all([
            insertedList.length > 0 ? postHRM2130_R01(insertedList.map(normalize)) : Promise.resolve(undefined),
            modifiedList.length > 0 ? patchHRM2130_R01(modifiedList.map(normalize)) : Promise.resolve(undefined),
            deleteTargetList.length > 0 ?
                deleteHRM2130_R01(deleteTargetList.map(row => row.attendCd).join(',')) : Promise.resolve(undefined),
        ]);
        // 실행하지 않은 요청(undefined)은 빼고, 실제로 보낸 요청의 성공 여부만 본다.
        // 타입 가드: filter 결과에서 undefined가 빠졌다고 TypeScript에 알려 준다.
        const sentResponseList = responseList.filter(
            (response): response is NonNullable<typeof response> => response !== undefined);
        // 공통 인터셉터는 실패 응답도 서버 안내 문장으로 알린 뒤 그대로 넘겨주므로, 하나라도 실패하면 완료 알림을 띄우지 않는다.
        if (!sentResponseList.every(response => response.data.successFlag)) {
            // 세 요청은 각각 따로 저장되므로 일부만 성공했으면 DB 상태를 화면에 맞추기 위해 재조회한다.
            // 모두 실패했으면 사용자가 고쳐서 다시 저장할 수 있도록 편집 상태를 그대로 둔다.
            if (sentResponseList.some(response => response.data.successFlag)) {
                await executeDataFetch('refetch', { messageMode: 'none' });
            }
            return;
        }
        showSuccess('근태코드 저장이 완료되었습니다.');
        setMessage('근태코드 저장이 완료되었습니다.');
        await executeDataFetch('refetch', { messageMode: 'none' });
    }, [gridRef, deletedHRM2130_R01List, HRM2130_R01List, validateInsertData,
        setMessage, executeDataFetch]);

    /**
     * 삭제 버튼의 표시·복구 동작을 처리한다. 신규 행은 즉시 목록에서 제거한다.
     * @returns 반환값 없이 선택 및 삭제 표시 상태를 갱신한다.
     */
    const handleDeleteClick = useCallback(() => {
        // 삭제 표시가 이미 있으면 버튼 동작을 전체 복구로 처리한다.
        if (deletedHRM2130_R01List.length > 0) {
            removeDeletedHRM2130_R01List();
            removeSelectedHRM2130_R01List();
            return;
        }
        // 선택된 행이 없으면 삭제할 대상이 없으므로 종료한다.
        if (selectedHRM2130_R01List.length === 0) return;
        // 선택 목록에는 숫자(행 클릭)와 문자열(전체 선택)이 섞일 수 있어 문자열로 맞춰 비교한다.
        const selectedSet = new Set(selectedHRM2130_R01List.map(String));
        const newRows = HRM2130_R01List.filter(row => selectedSet.has(String(row.valueIndex)) && row.inserted);
        newRows.forEach(row => removeHRM2130_R01List(row.valueIndex));
        // 선택 목록의 값을 형 변환 없이 그대로 삭제 목록으로 옮긴다.
        // 행 클릭 Hook과 행 번호 D 표시가 숫자 그대로 비교하므로, 같은 형을 유지해야 삭제 행 선택 차단과 D 표시가 맞는다. (PRD2010_R01 표준과 동일)
        const existingIds = selectedHRM2130_R01List.filter(id =>
            HRM2130_R01List.some(row => String(row.valueIndex) === String(id) && !row.inserted));
        // 서버에 이미 존재하는 행만 저장 시 삭제할 목록에 표시한다.
        if (existingIds.length > 0) addDeletedHRM2130_R01List(existingIds);
        removeSelectedHRM2130_R01List();
    }, [deletedHRM2130_R01List, selectedHRM2130_R01List, HRM2130_R01List,
        removeDeletedHRM2130_R01List, removeSelectedHRM2130_R01List,
        removeHRM2130_R01List, addDeletedHRM2130_R01List]);

    /**
     * 현재 검색 조건으로 전체 Excel 데이터를 요청하고 파일 다운로드를 시작한다.
     * @returns 다운로드 응답 처리가 끝나는 Promise<void>. 오류는 호출자에게 전파한다.
     */
    const handleExcelClick = useCallback(async () => {
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_HRM2130_R01('search', getParams(HRM2130_R01Filter), 0);
            // 실패 응답은 파일 데이터가 없으므로 다운로드하지 않는다. 알림은 공통 인터셉터가 한 번 띄운다.
            if (!response.data.successFlag) return;
            downloadSettingExcel({ responseData: response.data.data, fileName: '근태코드 등록-excel.xlsx' });
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, getParams, HRM2130_R01Filter]);

    // 화면을 열 때 자동 조회하지 않고, 조회 버튼을 눌러야 조회한다. 목록이 비어 있으면(최초 진입·초기화·0건 조회)
    // 바로 입력할 수 있도록 빈 신규 행을 하나 넣는다. (PRD2010_R01 표준 패턴과 동일)
    // 의존성: 목록이 바뀔 때마다 비었는지 다시 확인한다.
    useEffect(() => {
        if (HRM2130_R01List.length === 0) {
            handleInsertClick({} as React.MouseEvent);
            // 등록 처리 중 행 클릭이 막히지 않도록 클릭 가능 상태로 되돌린다.
            setClickable(true);
        }
    }, [HRM2130_R01List, handleInsertClick]);

    // Container가 필요한 함수만 노출한다. 데이터 값은 Store에서 읽는다.
    return {
        clickable, setClickable, executeDataFetch,
        fetchData, defaultHRM2130_R01Row, initContainerData, handleInsertClick,
        handleSaveClick, handleDeleteClick, handleExcelClick,
    };
};
